import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../store/AuthContext";
import {
  CheckCircle2,
  Eye,
  Loader2,
  RefreshCw,
  Search,
  Send,
  X,
  XCircle,
} from "lucide-react";
import { PageHeader } from "../../components/common/PageHeader";
import { StatCard } from "../../components/common/StatCard";
import { StatusBadge } from "../../components/common/StatusBadge";
import { RestaurantSettlementService } from "../../services";
import { currency, dateTime } from "../../utils/format";
import { sriLankaYear } from "../../utils/sriLankaTime";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const PRIMARY_BUTTON_CLASS =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-navy-800 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-navy-900 disabled:cursor-not-allowed disabled:opacity-60";

const OUTLINE_BUTTON_CLASS =
  "inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60";

const money = (value) => currency(Number(value || 0));

const isSystemStartDate = (value) => {
  if (!value) return true;
  const year = sriLankaYear(value);
  return year != null && year <= 2020;
};

const lastSettlementLabel = (value) => {
  if (isSystemStartDate(value)) return "No previous owner settlement";
  return dateTime(value);
};

const safeDateTime = (value) => (value ? dateTime(value) : "-");

const Dialog = ({ dialog, loading, onClose }) => {
  if (!dialog.open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/50 p-0 sm:items-center sm:p-4">
      <div className="max-h-[92vh] w-full overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:max-w-2xl sm:rounded-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-5">
          <div>
            <h3 className="font-display text-lg font-semibold text-slate-900">
              {dialog.title}
            </h3>
            {dialog.subtitle && (
              <p className="mt-1 text-sm text-slate-500">{dialog.subtitle}</p>
            )}
          </div>

          {!readOnly && <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>}
        </div>

        <div className="max-h-[65vh] overflow-y-auto p-5">{dialog.body}</div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-100 p-5 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className={OUTLINE_BUTTON_CLASS}
          >
            {dialog.cancelLabel || "Close"}
          </button>

          {dialog.onConfirm && (
            <button
              type="button"
              onClick={dialog.onConfirm}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-navy-800 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {dialog.confirmLabel || "Confirm"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, bold }) => (
  <div className="grid grid-cols-1 gap-1 rounded-lg border border-slate-100 bg-slate-50 p-3 sm:grid-cols-[170px_1fr] sm:items-center">
    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
      {label}
    </span>
    <span
      className={
        bold
          ? "font-display text-base font-semibold text-slate-950"
          : "text-sm text-slate-800"
      }
    >
      {value || "-"}
    </span>
  </div>
);

const Pagination = ({ totalItems, page, pageSize, setPage, setPageSize }) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const start = totalItems === 0 ? 0 : page * pageSize + 1;
  const end = Math.min(totalItems, (page + 1) * pageSize);
  const canPrevious = page > 0;
  const canNext = page < totalPages - 1;

  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <span className="text-slate-500">Rows per page</span>
        <select
          value={pageSize}
          onChange={(event) => {
            setPageSize(Number(event.target.value));
            setPage(0);
          }}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none"
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
        <span className="text-slate-500">{start}–{end} of {totalItems}</span>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={() => setPage((prev) => Math.max(0, prev - 1))} disabled={!canPrevious} className="rounded-lg border border-slate-200 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-40">Previous</button>
        <span className="px-2 text-slate-700">Page {page + 1} / {totalPages}</span>
        <button type="button" onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))} disabled={!canNext} className="rounded-lg border border-slate-200 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-40">Next</button>
      </div>
    </div>
  );
};

export const RestaurantOwnerSettlementPage = () => {
  const [searchParams] = useSearchParams();
  const { role } = useAuth();
  const businessDate = searchParams.get("businessDate") || undefined;
  const readOnly = role === "RESTAURANT";
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [actionLoadingKey, setActionLoadingKey] = useState("");

  const [filters, setFilters] = useState({
    status: "ALL",
    search: "",
  });

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [dialog, setDialog] = useState({ open: false });

  const closeDialog = () => {
    if (actionLoadingKey) return;
    setDialog({ open: false });
  };

  const showMessage = (title, message, type = "success") => {
    const isError = type === "error";
    setDialog({
      open: true,
      title,
      body: (
        <div
          className={`flex gap-3 rounded-lg border p-3 text-sm ${
            isError
              ? "border-rose-200 bg-rose-50 text-rose-700"
              : "border-emerald-200 bg-emerald-50 text-emerald-700"
          }`}
        >
          {isError ? <XCircle className="h-5 w-5 shrink-0" /> : <CheckCircle2 className="h-5 w-5 shrink-0" />}
          <p>{message}</p>
        </div>
      ),
      cancelLabel: "OK",
    });
  };

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const data = await RestaurantSettlementService.current(businessDate);
      setSummary(data || null);
    } catch (error) {
      showMessage(
        "Unable to load owner settlement summary",
        error?.response?.data?.message || error?.message || "Please refresh and try again.",
        "error"
      );
    } finally {
      setSummaryLoading(false);
    }
  }, [businessDate]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await RestaurantSettlementService.ownerSettlements(businessDate);
      setHistory(data || []);
    } catch (error) {
      showMessage(
        "Unable to load owner settlement history",
        error?.response?.data?.message || error?.message || "Please refresh and try again.",
        "error"
      );
    } finally {
      setHistoryLoading(false);
    }
  }, [businessDate]);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadSummary(), loadHistory()]);
  }, [loadSummary, loadHistory]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const filteredRows = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return (history || []).filter((row) => {
      if (filters.status !== "ALL" && row.status !== filters.status) return false;
      const searchable = [row.status, row.paidAmount, row.cashierNote, row.adminNote, row.confirmedByName, row.rejectedByName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return !search || searchable.includes(search);
    });
  }, [history, filters]);

  useEffect(() => {
    setPage(0);
  }, [filters]);

  const pagedRows = useMemo(() => {
    const start = page * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const pendingCount = useMemo(() => filteredRows.filter((row) => row.status === "PENDING").length, [filteredRows]);

  const confirmedAmount = useMemo(
    () => filteredRows.filter((row) => row.status === "CONFIRMED").reduce((sum, row) => sum + Number(row.paidAmount || 0), 0),
    [filteredRows]
  );

  const latestSettlement = useMemo(() => history?.[0] || null, [history]);

  const ownerPayable = Number(summary?.ownerPayable || 0);
  const hasPendingPreviousRequest = Boolean(summary?.existingOwnerSettlementStatus === "PENDING");

  const requestOwnerSettlement = () => {
    if (!ownerPayable || ownerPayable <= 0) {
      showMessage(
        "No owner payable",
        "There is no new owner payable amount available right now.",
        "error"
      );
      return;
    }

    const autoNote = `Paid to Owner ${money(ownerPayable)}`;

    setDialog({
      open: true,
      title: "Create Owner Settlement",
      subtitle: "This creates a pending request for the new amount currently available.",
      body: (
        <div className="space-y-4 text-sm">
          <div className="space-y-3 rounded-xl border border-slate-100 bg-slate-50 p-4">
            <Row label="Owner Payable" value={money(ownerPayable)} bold />
            <Row label="Calculated From" value={safeDateTime(summary?.periodStart)} />
            <Row label="Calculated Up To" value={safeDateTime(summary?.periodEnd)} />
            <Row label="Auto Note" value={autoNote} />
          </div>

          {hasPendingPreviousRequest && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800">
              There is already a pending owner request. This new request covers only the amount collected after that request.
            </div>
          )}
        </div>
      ),
      confirmLabel: "Create Settlement",
      onConfirm: async () => {
        setActionLoadingKey("owner-settlement");
        try {
          await RestaurantSettlementService.requestOwnerSettlement({
            settlementType: "RESTAURANT_OWNER",
            paidAmount: ownerPayable,
            cashierNote: null,
            businessDate,
          });

          setDialog({ open: false });
          showMessage("Owner settlement requested", "Owner settlement request was created successfully.", "success");
          await refreshAll();
        } catch (error) {
          showMessage(
            "Request failed",
            error?.response?.data?.message || error?.message || "Could not create Owner Settlement request.",
            "error"
          );
        } finally {
          setActionLoadingKey("");
        }
      },
      cancelLabel: "Close",
    });
  };

  const openDetails = (row) => {
    setDialog({
      open: true,
      title: "Owner Settlement Details",
      subtitle: `Status: ${row.status}`,
      body: (
        <div className="space-y-3 text-sm">
          <Row label="Amount Paid" value={money(row.paidAmount)} bold />
          <Row label="Status" value={<StatusBadge value={row.status} />} />
          <Row label="Period From" value={safeDateTime(row.periodStart)} />
          <Row label="Period To" value={safeDateTime(row.periodEnd)} />
          <Row label="Requested At" value={safeDateTime(row.requestedAt)} />
          <Row label="Request Note" value={row.cashierNote} />
          {row.confirmedByName && <Row label="Confirmed By" value={row.confirmedByName} />}
          {row.confirmedAt && <Row label="Confirmed At" value={safeDateTime(row.confirmedAt)} />}
          {row.rejectedByName && <Row label="Rejected By" value={row.rejectedByName} />}
          {row.rejectedAt && <Row label="Rejected At" value={safeDateTime(row.rejectedAt)} />}
          {row.adminNote && <Row label="Owner Note / Reason" value={row.adminNote} />}
        </div>
      ),
      cancelLabel: "Close",
    });
  };

  return (
    <div>
      <PageHeader
        title="Owner Settlement"
        subtitle="Create owner settlement requests for new available amounts and view history."
        actions={
          <button type="button" onClick={refreshAll} disabled={summaryLoading || historyLoading} className={PRIMARY_BUTTON_CLASS}>
            {summaryLoading || historyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Owner Payable" value={summaryLoading ? "…" : money(ownerPayable)} icon="Banknote" tone="navy" />
        <StatCard label="Pending Requests" value={historyLoading ? "…" : pendingCount} icon="Receipt" tone="gold" />
        <StatCard label="Confirmed Amount" value={historyLoading ? "…" : money(confirmedAmount)} icon="BadgeDollarSign" tone="emerald" />
        <StatCard label="Latest Status" value={historyLoading ? "…" : latestSettlement?.status || "-"} icon="Receipt" tone="sky" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-[420px_1fr]">
        <div className="bms-card p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-display text-lg font-semibold">Current Owner Settlement</h3>
              <p className="mt-1 text-sm leading-6 text-slate-500">
                Request only the new payable amount available after the last covered owner settlement.
              </p>
            </div>
            {hasPendingPreviousRequest && <StatusBadge value="PENDING PREVIOUS REQUEST" />}
          </div>

          <div className="mt-4 space-y-3 rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm">
            <Row label="Available to Request" value={money(ownerPayable)} bold />
            <Row label="Last Owner Settlement" value={lastSettlementLabel(summary?.lastOwnerPaymentDate)} />
            <Row label="Calculated From" value={safeDateTime(summary?.periodStart)} />
            <Row label="Calculated Up To" value={safeDateTime(summary?.periodEnd)} />
            <Row label="Auto Note" value={`Paid to Owner ${money(ownerPayable)}`} />
          </div>

          {ownerPayable <= 0 && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
              No new owner payable amount is available right now.
            </div>
          )}

          {hasPendingPreviousRequest && ownerPayable > 0 && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              A previous owner request is still pending. This screen is showing only the new amount collected after that request.
            </div>
          )}

          <button
            type="button"
            onClick={requestOwnerSettlement}
            disabled={actionLoadingKey === "owner-settlement" || ownerPayable <= 0}
            className={PRIMARY_BUTTON_CLASS + " mt-4 w-full"}
          >
            {actionLoadingKey === "owner-settlement" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Request Owner Confirmation
          </button>
        </div>

        <div className="bms-card p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h3 className="font-display text-lg font-semibold">Payment History</h3>
              <p className="mt-1 text-sm text-slate-500">Owner settlement requests and owner decisions.</p>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[170px_240px]">
              <select value={filters.status} onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))} className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none">
                <option value="ALL">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="CONFIRMED">Confirmed</option>
                <option value="REJECTED">Rejected</option>
                <option value="REVERSED">Reversed</option>
              </select>

              <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
                <Search className="h-4 w-4 text-slate-400" />
                <input type="text" value={filters.search} onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))} className="w-full bg-transparent text-sm outline-none" placeholder="Search" />
              </div>
            </div>
          </div>

          <div className="mt-5 overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-500">Requested</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-500">Amount</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-500">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-500">Note</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-500">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {pagedRows.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No owner settlement payment history found.</td></tr>
                ) : (
                  pagedRows.map((row) => (
                    <tr key={row.settlementId}>
                      <td className="px-4 py-3 text-slate-700">{safeDateTime(row.requestedAt)}</td>
                      <td className="px-4 py-3 text-right font-semibold">{money(row.paidAmount)}</td>
                      <td className="px-4 py-3"><StatusBadge value={row.status} /></td>
                      <td className="px-4 py-3 text-slate-700">{row.cashierNote || "-"}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <button type="button" onClick={() => openDetails(row)} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
                            <Eye className="h-3 w-3" />
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination totalItems={filteredRows.length} page={page} pageSize={pageSize} setPage={setPage} setPageSize={setPageSize} />
        </div>
      </div>

      <Dialog dialog={dialog} loading={Boolean(actionLoadingKey)} onClose={closeDialog} />
    </div>
  );
};
